import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import {
	User,
	Mail,
	Phone,
	Lock,
	Shield,
	CheckCircle,
	Store,
	Sparkles,
	Award,
	Sofa,
	Truck,
	Package,
	Headphones,
	Clock,
	ArrowLeft
} from 'lucide-react';
import { FaEye, FaEyeSlash, FaStore, FaChartLine, FaShieldAlt } from 'react-icons/fa';

const CreateAccount = () => {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: '',
		password: '',
		confirmPassword: ''
	});
	const [stage, setStage] = useState('DETAILS'); // DETAILS or OTP
	const [otp, setOtp] = useState('');
	const [loading, setLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [timer, setTimer] = useState(0);
	const [canResend, setCanResend] = useState(false);
	const [lastOtp, setLastOtp] = useState(null);
	const [animate, setAnimate] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	// Timer useEffect
	React.useEffect(() => {
		let interval;
		if (timer > 0) {
			interval = setInterval(() => {
				setTimer(prev => prev - 1);
			}, 1000);
		} else if (timer === 0 && stage === 'OTP') {
			setCanResend(true);
		}
		return () => clearInterval(interval);
	}, [timer, stage]);

	// Animation effect
	React.useEffect(() => {
		setAnimate(true);
		const timer = setTimeout(() => setAnimate(false), 1000);
		return () => clearTimeout(timer);
	}, [stage]);

	// Auto-fill OTP if available
	React.useEffect(() => {
		if (lastOtp && stage === 'OTP') {
			setOtp(lastOtp.toString());
		}
	}, [lastOtp, stage]);

	const validateForm = () => {
		const { name, email, phone, password, confirmPassword } = formData;

		if (!name || !email || !phone || !password || !confirmPassword) {
			return 'All fields are required.';
		}

		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			return 'Invalid email format.';
		}

		const phoneRegex = /^[0-9]{10}$/;
		if (!phoneRegex.test(phone)) {
			return 'Phone number must be exactly 10 digits.';
		}

		if (password.length < 6) {
			return 'Password must be at least 6 characters.';
		}

		if (password !== confirmPassword) {
			return 'Passwords do not match.';
		}

		return '';
	};

	const handleChange = (e) => {
		setFormData(prev => ({
			...prev,
			[e.target.name]: e.target.value
		}));
	};

	const handleOtpChange = (e) => {
		const value = e.target.value.replace(/\D/g, '').slice(0, 6);
		setOtp(value);
	};

	const handleSendOtp = async (e) => {
		e.preventDefault();
		setMessage('');

		const validationError = validateForm();
		if (validationError) {
			setMessage(validationError);
			toast.error(validationError);
			return;
		}

		setLoading(true);
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/seller/register/send-otp`,
				formData,
				{ headers: { 'Content-Type': 'application/json' } }
			);

			if (response.data.otp) {
				setLastOtp(response.data.otp);
				toast.success('OTP sent successfully! Check your phone.');
			} else {
				toast.success('OTP sent to your phone!');
			}

			setStage('OTP');
			setTimer(60);
			setCanResend(false);
			setOtp('');
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to send OTP.';
			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleResendOtp = async () => {
		if (!canResend) return;

		setLoading(true);
		try {
			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/seller/register/send-otp`,
				formData,
				{ headers: { 'Content-Type': 'application/json' } }
			);

			if (response.data.otp) {
				setLastOtp(response.data.otp);
				toast.success('OTP resent successfully!');
			} else {
				toast.success('OTP resent to your phone!');
			}

			setTimer(60);
			setCanResend(false);
			setOtp('');
		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Failed to resend OTP.';
			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleVerifyOtp = async (e) => {
		e.preventDefault();

		if (otp.length !== 6) {
			setMessage('OTP must be 6 digits');
			toast.error('OTP must be 6 digits');
			return;
		}

		setLoading(true);
		try {
			const payload = {
				phone: formData.phone,
				otp: otp,
				name: formData.name,
				email: formData.email,
				password: formData.password
			};

			const response = await axios.post(
				`${process.env.REACT_APP_BACKEND_URL}/api/seller/register/verify-otp`,
				payload,
				{ headers: { 'Content-Type': 'application/json' } }
			);

			toast.success('Registration successful! Welcome to Zakkuu Seller Panel!');

			// Redirect to seller login
			setTimeout(() => {
				window.location.href = 'https://seller.zakkuu.com/seller/login';
			}, 2000);

		} catch (error) {
			const errorMessage = error?.response?.data?.message || 'Registration failed.';
			setMessage(errorMessage);
			toast.error(errorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleBack = () => {
		setStage('DETAILS');
		setMessage('');
		setTimer(0);
		setCanResend(false);
		setLastOtp(null);
		setOtp('');
	};

	const formatTime = (seconds) => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	};

	return (
		<div className="min-h-screen bg-gradient-to-br from-indigo-50 to-gray-50 flex flex-col lg:flex-row">
			{/* Left side - Branding & Features */}
			<div className="lg:w-1/2 bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 p-8 lg:p-12 flex flex-col justify-between relative overflow-hidden">
				{/* Decorative Elements */}
				<div className="absolute top-0 left-0 w-full h-full opacity-10">
					<div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-400 rounded-full"></div>
					<div className="absolute -bottom-20 -right-20 w-80 h-80 bg-purple-500 rounded-full"></div>
					<div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-blue-400 rounded-full"></div>
				</div>

				{/* Logo & Brand */}
				<div className="relative z-10">
					<div className="flex items-center gap-4 mb-10">
						<div className="relative">
							<div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-xl">
								<Sofa className="w-9 h-9 text-white" />
							</div>
							<div className="absolute -bottom-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
								<Sparkles className="w-4 h-4 text-white" />
							</div>
						</div>
						<div>
							<h1 className="text-4xl font-bold text-white tracking-tight">
								ZAKKUU
							</h1>
							<div className="flex items-center gap-2 mt-1">
								<div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
								<p className="text-indigo-200 text-lg font-medium">Become a Seller</p>
							</div>
						</div>
					</div>

					{/* Welcome Section */}
					<div className="mb-10">
						<div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500/20 to-yellow-500/20 backdrop-blur-sm rounded-full mb-6 border border-amber-300/30">
							<Award className="w-5 h-5 text-yellow-300" />
							<span className="text-yellow-200 text-base font-semibold">Start Your Business Journey</span>
							<Store className="w-5 h-5 text-yellow-300" />
						</div>
						<h2 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
							Join Our
							<span className="block text-indigo-200 mt-3">Premium Marketplace</span>
						</h2>
						<p className="text-indigo-100 text-xl leading-relaxed mb-8 max-w-2xl">
							Sell your premium furniture to thousands of discerning customers
							and grow your business with Zakkuu's powerful seller platform.
						</p>
					</div>
				</div>

				{/* Seller Benefits */}
				<div className="relative z-10">
					<h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
						<FaChartLine className="w-6 h-6 text-yellow-300" />
						Why Sell With Zakkuu?
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="flex items-start gap-4 p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl backdrop-blur-sm border border-white/10">
							<div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
								<Package className="w-6 h-6 text-white" />
							</div>
							<div>
								<h4 className="font-bold text-white text-lg mb-2">Premium Customer Base</h4>
								<p className="text-indigo-200 text-sm leading-relaxed">
									Access customers who value quality furniture and are willing to pay premium prices
								</p>
							</div>
						</div>

						<div className="flex items-start gap-4 p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl backdrop-blur-sm border border-white/10">
							<div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
								<Truck className="w-6 h-6 text-white" />
							</div>
							<div>
								<h4 className="font-bold text-white text-lg mb-2">Complete Logistics</h4>
								<p className="text-indigo-200 text-sm leading-relaxed">
									We handle delivery, returns, and customer service so you can focus on your products
								</p>
							</div>
						</div>

						<div className="flex items-start gap-4 p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl backdrop-blur-sm border border-white/10">
							<div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
								<FaShieldAlt className="w-6 h-6 text-white" />
							</div>
							<div>
								<h4 className="font-bold text-white text-lg mb-2">Secure Payments</h4>
								<p className="text-indigo-200 text-sm leading-relaxed">
									Guaranteed weekly settlements with complete financial transparency
								</p>
							</div>
						</div>

						<div className="flex items-start gap-4 p-5 bg-gradient-to-r from-white/5 to-white/10 rounded-2xl backdrop-blur-sm border border-white/10">
							<div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
								<Headphones className="w-6 h-6 text-white" />
							</div>
							<div>
								<h4 className="font-bold text-white text-lg mb-2">Dedicated Support</h4>
								<p className="text-indigo-200 text-sm leading-relaxed">
									Get 24/7 seller support and account management for your business growth
								</p>
							</div>
						</div>
					</div>
				</div>

				{/* Stats Section */}
				<div className="relative z-10 mt-10">
					<div className="grid grid-cols-3 gap-4">
						<div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
							<div className="text-2xl font-bold text-white">500+</div>
							<div className="text-indigo-200 text-sm">Active Sellers</div>
						</div>
						<div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
							<div className="text-2xl font-bold text-white">10K+</div>
							<div className="text-indigo-200 text-sm">Monthly Orders</div>
						</div>
						<div className="text-center p-4 bg-white/5 rounded-xl backdrop-blur-sm">
							<div className="text-2xl font-bold text-white">₹2Cr+</div>
							<div className="text-indigo-200 text-sm">Monthly GMV</div>
						</div>
					</div>
				</div>
			</div>

			{/* Right side - Registration Form */}
			<div className="lg:w-1/2 p-6 sm:p-8 lg:p-12 flex items-center justify-center">
				<div className={`w-full max-w-lg transition-all duration-500 ${animate ? 'scale-105' : 'scale-100'}`}>
					<div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 border border-indigo-100">
						{/* Header */}
						<div className="text-center mb-8">
							<div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded-3xl mb-4 shadow-lg">
								<div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-2xl flex items-center justify-center">
									<Store className="w-8 h-8 text-white" />
								</div>
							</div>
							<h2 className="text-3xl font-bold text-gray-800 mb-2">
								{stage === 'DETAILS' ? 'Create Seller Account' : 'Verify OTP'}
							</h2>
							<p className="text-gray-600">
								{stage === 'DETAILS'
									? 'Start your furniture selling journey with Zakkuu'
									: 'Enter OTP sent to your phone'}
							</p>

							{/* Progress Indicator */}
							<div className="flex items-center justify-center gap-4 mt-4">
								<div className={`w-3 h-3 rounded-full ${stage === 'DETAILS' ? 'bg-indigo-600' : 'bg-green-500'}`}></div>
								<div className="w-16 h-1 bg-gray-200 rounded-full">
									<div className={`h-full rounded-full ${stage === 'DETAILS' ? 'w-1/2 bg-indigo-300' : 'w-full bg-green-400'}`}></div>
								</div>
								<div className={`w-3 h-3 rounded-full ${stage === 'DETAILS' ? 'bg-gray-300' : 'bg-indigo-600'}`}></div>
							</div>
						</div>

						{/* Registration Form */}
						<form onSubmit={stage === 'DETAILS' ? handleSendOtp : handleVerifyOtp}>
							{stage === 'DETAILS' ? (
								<div className="space-y-6">
									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										{/* Full Name */}
										<div>
											<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
												<User className="w-4 h-4 text-indigo-600" />
												Full Name
											</label>
											<div className="relative">
												<input
													type="text"
													name="name"
													value={formData.name}
													onChange={handleChange}
													className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-lg"
													placeholder="Enter your name"
													required
												/>
											</div>
										</div>

										{/* Email */}
										<div>
											<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
												<Mail className="w-4 h-4 text-indigo-600" />
												Email Address
											</label>
											<div className="relative">
												<input
													type="email"
													name="email"
													value={formData.email}
													onChange={handleChange}
													className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-lg"
													placeholder="your@email.com"
													required
												/>
											</div>
										</div>
									</div>

									{/* Phone */}
									<div>
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
											<Phone className="w-4 h-4 text-indigo-600" />
											Phone Number
										</label>
										<div className="relative">
											<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
												<span className="text-gray-500 font-medium">+91</span>
											</div>
											<input
												type="tel"
												name="phone"
												value={formData.phone}
												onChange={handleChange}
												className="w-full pl-14 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-lg"
												placeholder="10-digit number"
												maxLength="10"
												required
											/>
										</div>
									</div>

									<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
										{/* Password */}
										<div>
											<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
												<Lock className="w-4 h-4 text-indigo-600" />
												Password
											</label>
											<div className="relative">
												<input
													type={showPassword ? "text" : "password"}
													name="password"
													value={formData.password}
													onChange={handleChange}
													className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-lg"
													placeholder="••••••••"
													required
												/>
												<button
													type="button"
													onClick={() => setShowPassword(!showPassword)}
													className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
												>
													{showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
												</button>
											</div>
										</div>

										{/* Confirm Password */}
										<div>
											<label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
												<CheckCircle className="w-4 h-4 text-indigo-600" />
												Confirm Password
											</label>
											<div className="relative">
												<input
													type={showConfirmPassword ? "text" : "password"}
													name="confirmPassword"
													value={formData.confirmPassword}
													onChange={handleChange}
													className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 text-lg"
													placeholder="••••••••"
													required
												/>
												<button
													type="button"
													onClick={() => setShowConfirmPassword(!showConfirmPassword)}
													className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
												>
													{showConfirmPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
												</button>
											</div>
										</div>
									</div>

									{/* Terms Agreement */}
									<div className="flex items-start gap-3">
										<input
											type="checkbox"
											id="terms"
											required
											className="w-5 h-5 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 mt-1"
										/>
										<label htmlFor="terms" className="text-gray-600 text-sm">
											I agree to Zakkuu's{' '}
											<a href="/seller/terms" className="text-indigo-600 hover:text-indigo-800 font-medium">Seller Terms</a>{' '}
											and{' '}
											<a href="/seller/privacy" className="text-indigo-600 hover:text-indigo-800 font-medium">Privacy Policy</a>.
											I confirm that all information provided is accurate.
										</label>
									</div>

									{/* Verification Note */}
									<div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
										<p className="text-blue-700 text-sm">
											<span className="font-medium">Note:</span> To verify your number, we will send a text message with a temporary code.
											Message and data rates may apply.
										</p>
									</div>

									{message && (
										<div className="p-3 bg-red-50 border border-red-100 rounded-xl">
											<p className="text-red-700 text-sm text-center">{message}</p>
										</div>
									)}

									<button
										type="submit"
										disabled={loading}
										className="w-full py-4 bg-gradient-to-r from-indigo-600 via-indigo-700 to-purple-700 text-white font-bold rounded-xl hover:from-indigo-700 hover:via-indigo-800 hover:to-purple-800 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-xl shadow-indigo-200 hover:shadow-indigo-300 text-lg"
									>
										{loading ? (
											<>
												<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
												Sending OTP...
											</>
										) : (
											<>
												<Sparkles className="w-6 h-6" />
												Continue with OTP
											</>
										)}
									</button>
								</div>
							) : (
								<div className="space-y-6">
									{/* Phone Display */}
									<div className="text-center p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-200">
										<div className="flex items-center justify-center gap-2 mb-2">
											<Phone className="w-4 h-4 text-indigo-600" />
											<p className="text-gray-600">OTP sent to</p>
										</div>
										<p className="text-xl font-semibold text-gray-800 tracking-wide">
											+91 ••••• •{formData.phone.slice(-3)}
										</p>
									</div>

									{/* OTP Display (if available) */}
									{lastOtp && (
										<div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-xl">
											<div className="flex items-center justify-between mb-2">
												<span className="text-sm font-medium text-indigo-800 flex items-center gap-2">
													<Mail className="w-4 h-4" />
													Development OTP
												</span>
												<span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full font-medium">Auto-filled</span>
											</div>
											<div className="text-center">
												<div className="text-3xl font-bold text-indigo-900 tracking-widest font-mono">
													{lastOtp}
												</div>
											</div>
										</div>
									)}

									{/* OTP Input */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Enter 6-Digit OTP
										</label>
										<div className="relative">
											<input
												type="text"
												value={otp}
												onChange={handleOtpChange}
												className="w-full px-4 py-3.5 text-center text-2xl font-bold tracking-widest bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 font-mono"
												placeholder="0 0 0 0 0 0"
												maxLength={6}
												inputMode="numeric"
												autoFocus
											/>
											<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
												<Shield className="w-5 h-5 text-indigo-500" />
											</div>
										</div>
									</div>

									{/* Resend OTP */}
									<div className="flex justify-between items-center">
										<button
											type="button"
											onClick={handleResendOtp}
											disabled={!canResend || loading}
											className={`text-sm flex items-center gap-2 ${canResend ? 'text-indigo-600 hover:text-indigo-800 font-medium' : 'text-gray-500'}`}
										>
											{canResend ? (
												<>
													<Clock className="w-4 h-4" />
													Resend OTP
												</>
											) : (
												<>
													<Clock className="w-4 h-4 animate-pulse" />
													Resend OTP in {formatTime(timer)}
												</>
											)}
										</button>

										<button
											type="button"
											onClick={handleBack}
											className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
										>
											<ArrowLeft className="w-4 h-4" />
											Back
										</button>
									</div>

									{message && (
										<div className="p-3 bg-red-50 border border-red-100 rounded-xl">
											<p className="text-red-700 text-sm text-center">{message}</p>
										</div>
									)}

									{/* Action Buttons */}
									<div className="flex gap-3">
										<button
											type="button"
											onClick={handleBack}
											className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-2"
											disabled={loading}
										>
											<ArrowLeft className="w-4 h-4" />
											Back
										</button>
										<button
											type="submit"
											disabled={loading || otp.length !== 6}
											className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold rounded-xl hover:from-indigo-700 hover:to-indigo-800 transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-indigo-200 hover:shadow-indigo-300"
										>
											{loading ? (
												<>
													<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
													Completing Registration...
												</>
											) : (
												<>
													<CheckCircle className="w-5 h-5" />
													Create Account
												</>
											)}
										</button>
									</div>
								</div>
							)}
						</form>

						{/* Divider */}
						<div className="my-6">
							<div className="relative">
								<div className="absolute inset-0 flex items-center">
									<div className="w-full border-t border-gray-200"></div>
								</div>
								<div className="relative flex justify-center text-sm">
									<span className="px-4 bg-white text-gray-500">
										{stage === 'DETAILS' ? 'Already a seller?' : ''}
									</span>
								</div>
							</div>
						</div>

						{/* Additional Links */}
						<div className="text-center space-y-4">
							{stage === 'DETAILS' && (
								<a
									href="https://seller.zakkuu.com/seller/login"
									className="inline-block w-full py-3 border-2 border-indigo-600 text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition-all duration-300 transform hover:scale-[1.02] flex items-center justify-center gap-2"
								>
									<User className="w-5 h-5" />
									Sign In to Seller Account
								</a>
							)}

							<p className="text-gray-600 text-sm px-4">
								Have questions?{' '}
								<a href="/seller/support" className="text-indigo-600 hover:underline font-medium">Contact Seller Support</a>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CreateAccount;